/**
 * FontAwesome icon wrappers with Lucide-compatible API.
 * Usage: <Flame className="h-4 w-4 text-primary" />
 */
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faGauge,
  faUtensils,
  faDumbbell,
  faUser,
  faRightFromBracket,
  faShield,
  faShieldHalved,
  faRuler,
  faSpinner,
  faFire,
  faDrumstickBite,
  faTrophy,
  faArrowTrendUp,
  faClock,
  faPlus,
  faPlay,
  faTrash,
  faCalendarDays,
  faRotateLeft,
  faEnvelope,
  faLock,
  faCalculator,
  faWheatAwn,
  faDroplet,
  faFloppyDisk,
  faBolt,
  faDollarSign,
  faMagnifyingGlass,
  faChevronLeft,
  faChevronRight,
  faChevronDown,
  faChevronUp,
  faCopy,
  faPencil,
  faCheck,
  faXmark,
  faWeightScale,
  faClockRotateLeft,
  faChartColumn,
  faBell,
  faBarcode,
  faKeyboard,
  faCircleCheck,
  faStopwatch,
  faLink,
  faGripVertical,
  faCamera,
  faSun,
  faMoon,
  faDesktop,
  faArrowLeft,
  faUsers,
  faGear,
  faKey,
  faBookOpen,
  faBullseye,
  faCircleInfo,
  faGlobe,
  faBan,
  faCircle,
  faTriangleExclamation,
  faCircleExclamation,
  faStar,
  faComment,
  faShareNodes,
  faUserTie,
  faPause,
  faRobot,
  faPaperPlane,
  faPhotoFilm,
  faShieldVirus,
  faWifi,
  faCloudArrowDown,
  faCloud,
  faCloudArrowUp,
  faArrowsRotate,
  faHardDrive,
  faBug,
  faMagnet,
  faStore,
  faEye,
  faHandshake,
  faMapPin,
  faVideo,
  faPhone,
  faCalendarCheck,
  faLocationDot,
  faBan as faBanSolid,
  faTag,
  faEyeSlash,
  faBullhorn,
  faWallet,
  faEllipsisVertical,
} from "@fortawesome/free-solid-svg-icons";
import { faStar as faStarRegular } from "@fortawesome/free-regular-svg-icons";

import { forwardRef } from "react";

interface IconProps {
  className?: string;
  [key: string]: any;
}

function createIcon(iconDef: IconDefinition, name: string) {
  const Component = forwardRef<SVGSVGElement, IconProps>(({ className, ...props }, ref) => (
    <FontAwesomeIcon icon={iconDef} className={className} {...props} ref={ref} />
  ));
  Component.displayName = name;
  return Component;
}

// Navigation & Layout
export const LayoutDashboard = createIcon(faGauge, "LayoutDashboard");
export const Utensils = createIcon(faUtensils, "Utensils");
export const Dumbbell = createIcon(faDumbbell, "Dumbbell");
export const User = createIcon(faUser, "User");
export const LogOut = createIcon(faRightFromBracket, "LogOut");
export const Shield = createIcon(faShieldHalved, "Shield");
export const Ruler = createIcon(faRuler, "Ruler");
export const Wallet = createIcon(faWallet, "Wallet");

// Status & Loading
export const Loader2 = createIcon(faSpinner, "Loader2");
export const Check = createIcon(faCheck, "Check");
export const X = createIcon(faXmark, "X");
export const Circle = createIcon(faCircle, "Circle");
export const CheckCircle2 = createIcon(faCircleCheck, "CheckCircle2");

// Food & Nutrition
export const Flame = createIcon(faFire, "Flame");
export const Beef = createIcon(faDrumstickBite, "Beef");
export const Wheat = createIcon(faWheatAwn, "Wheat");
export const Droplets = createIcon(faDroplet, "Droplets");

// Fitness
export const Trophy = createIcon(faTrophy, "Trophy");
export const Weight = createIcon(faWeightScale, "Weight");
export const Timer = createIcon(faStopwatch, "Timer");

// Charts & Trends
export const TrendingUp = createIcon(faArrowTrendUp, "TrendingUp");
export const BarChart3 = createIcon(faChartColumn, "BarChart3");

// Actions
export const Plus = createIcon(faPlus, "Plus");
export const Play = createIcon(faPlay, "Play");
export const Trash2 = createIcon(faTrash, "Trash2");
export const Save = createIcon(faFloppyDisk, "Save");
export const Copy = createIcon(faCopy, "Copy");
export const Pencil = createIcon(faPencil, "Pencil");
export const RotateCcw = createIcon(faRotateLeft, "RotateCcw");
export const Share2 = createIcon(faShareNodes, "Share2");
export const Search = createIcon(faMagnifyingGlass, "Search");

// Navigation arrows
export const ChevronLeft = createIcon(faChevronLeft, "ChevronLeft");
export const ChevronRight = createIcon(faChevronRight, "ChevronRight");
export const ChevronDown = createIcon(faChevronDown, "ChevronDown");
export const ChevronUp = createIcon(faChevronUp, "ChevronUp");
export const ArrowLeft = createIcon(faArrowLeft, "ArrowLeft");

// Time & Calendar
export const Clock = createIcon(faClock, "Clock");
export const CalendarDays = createIcon(faCalendarDays, "CalendarDays");
export const History = createIcon(faClockRotateLeft, "History");

// Forms & Auth
export const Mail = createIcon(faEnvelope, "Mail");
export const Lock = createIcon(faLock, "Lock");
export const Calculator = createIcon(faCalculator, "Calculator");
export const Zap = createIcon(faBolt, "Zap");
export const DollarSign = createIcon(faDollarSign, "DollarSign");

// Misc
export const Bell = createIcon(faBell, "Bell");
export const ScanBarcode = createIcon(faBarcode, "ScanBarcode");
export const Keyboard = createIcon(faKeyboard, "Keyboard");
export const Link2 = createIcon(faLink, "Link2");
export const GripVertical = createIcon(faGripVertical, "GripVertical");
export const Camera = createIcon(faCamera, "Camera");
export const Sun = createIcon(faSun, "Sun");
export const Moon = createIcon(faMoon, "Moon");
export const Monitor = createIcon(faDesktop, "Monitor");
export const Globe = createIcon(faGlobe, "Globe");
export const BookOpen = createIcon(faBookOpen, "BookOpen");

// Admin
export const Users = createIcon(faUsers, "Users");
export const Settings = createIcon(faGear, "Settings");
export const Key = createIcon(faKey, "Key");
export const ShieldCheck = createIcon(faShield, "ShieldCheck");
export const ShieldOff = createIcon(faShield, "ShieldOff");
export const Ban = createIcon(faBan, "Ban");

// Alerts
export const AlertTriangle = createIcon(faTriangleExclamation, "AlertTriangle");
export const AlertCircle = createIcon(faCircleExclamation, "AlertCircle");
export const Info = createIcon(faCircleInfo, "Info");
export const Target = createIcon(faBullseye, "Target");
export const PanelLeft = createIcon(faGauge, "PanelLeft");

// Feedback
export const Star = createIcon(faStar, "Star");
export const StarOutline = createIcon(faStarRegular, "StarOutline");
export const MessageSquare = createIcon(faComment, "MessageSquare");

// Coach
export const UserTie = createIcon(faUserTie, "UserTie");
export const Pause = createIcon(faPause, "Pause");
export const Bot = createIcon(faRobot, "Bot");
export const Send = createIcon(faPaperPlane, "Send");
export const MediaLibrary = createIcon(faPhotoFilm, "MediaLibrary");
export const ShieldAlert = createIcon(faShieldVirus, "ShieldAlert");

// Offline / connectivity
export const WifiOff = createIcon(faWifi, "WifiOff");
export const Wifi = createIcon(faWifi, "Wifi");
export const Cloud = createIcon(faCloud, "Cloud");
export const CloudOff = createIcon(faCloudArrowUp, "CloudOff");
export const Download = createIcon(faCloudArrowDown, "Download");
export const RefreshCw = createIcon(faArrowsRotate, "RefreshCw");
export const HardDrive = createIcon(faHardDrive, "HardDrive");
export const Bug = createIcon(faBug, "Bug");
export const Magnet = createIcon(faMagnet, "Magnet");
export const Store = createIcon(faStore, "Store");
export const Eye = createIcon(faEye, "Eye");
export const Handshake = createIcon(faHandshake, "Handshake");
export const MapPin = createIcon(faMapPin, "MapPin");
export const Video = createIcon(faVideo, "Video");
export const Phone = createIcon(faPhone, "Phone");
export const CalendarCheck = createIcon(faCalendarCheck, "CalendarCheck");
export const MapPinned = createIcon(faLocationDot, "MapPinned");

// Carousel (used in shadcn)
export const ArrowRight = createIcon(faChevronRight, "ArrowRight");

// Promo
export const Tag = createIcon(faTag, "Tag");
export const EllipsisVertical = createIcon(faEllipsisVertical, "EllipsisVertical");

// Ad management
export const EyeOff = createIcon(faEyeSlash, "EyeOff");
export const Megaphone = createIcon(faBullhorn, "Megaphone");
